// Shared bits of the public phone-verification gate, used by both
// ApplicantRegistrationForm (registro) and ApplicantContinue (login + edición
// de perfil) so the three send paths behave identically.

import { useCallback, useEffect, useRef, useState } from "react";

// Seconds the resend button stays disabled after a send. Rapid re-sends are
// what trip auth/too-many-requests and Google's silent anti-fraud throttling,
// which then looks like "the SMS never arrives".
export const RESEND_COOLDOWN_SECONDS = 60;

// Whether the "modo de prueba SMS" banner may be shown at all. The public form
// is unauthenticated, so the fact that an environment runs in test mode is
// internal state: staff see it in a dev build (and on the admin config screen,
// components/applicants/config/ApplicantConfig.js), citizens never do.
export const SMS_TEST_MODE_VISIBLE =
  !process.env.NODE_ENV || process.env.NODE_ENV === "development";

// Copy shown right after a send. Deliberately does NOT claim delivery:
// signInWithPhoneNumber resolves when Google *accepts* the request, which is
// not the same as the SMS arriving. Echoing the normalized E.164 also exposes a
// bad toE164 result (e.g. a leading zero producing +570300…) that would
// otherwise be sent to Firebase unnoticed.
export const phoneSendInfo = (e164, testMode) =>
  testMode
    ? `Modo de prueba: no se envió un SMS real a ${e164}. Usa el código de prueba configurado.`
    : `Enviamos un código a ${e164}. Puede tardar hasta un minuto en llegar.`;

// Shown under the code field while waiting for the SMS.
export const NO_SMS_HINT =
  "¿No te llegó? Revisa que el número mostrado sea exactamente el tuyo, espera un minuto y vuelve a solicitarlo.";

// Countdown that disables the resend button. `start()` is called after each
// successful send; `seconds` is 0 when resending is allowed again.
export function useResendCooldown(durationSeconds = RESEND_COOLDOWN_SECONDS) {
  const [seconds, setSeconds] = useState(0);
  const timerRef = useRef(null);

  useEffect(() => () => clearInterval(timerRef.current), []);

  const start = useCallback(() => {
    clearInterval(timerRef.current);
    setSeconds(durationSeconds);
    timerRef.current = setInterval(() => {
      setSeconds((s) => {
        if (s <= 1) {
          clearInterval(timerRef.current);
          return 0;
        }
        return s - 1;
      });
    }, 1000);
  }, [durationSeconds]);

  const reset = useCallback(() => {
    clearInterval(timerRef.current);
    setSeconds(0);
  }, []);

  return { seconds, start, reset };
}
